import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import { Download, ExternalLink, FileUser, Upload, X } from "lucide-react";
import { CTA_TITLES } from "components/UserCardList/constants/ctaTitles";
import { getResumeUrls } from "utils/helpers/resume";
import { getUserName } from "utils/helpers/users";

const ResumePreviewModal = ({
  isOpen,
  onClose,
  currentUser,
  onInitiateUpload,
}) => {
  const [activeResumeKey, setActiveResumeKey] = useState("application");
  const [pdfErrors, setPdfErrors] = useState({});
  const applicationResumeLocation =
    currentUser?.match?.resumeLocation ||
    currentUser?.associatedMatch?.resumeLocation;
  const userResumeLocation = currentUser?.resumeLocation;

  const resumeTabs = useMemo(() => {
    if (!applicationResumeLocation && !userResumeLocation) {
      return [];
    }

    return [
      {
        key: "application",
        label: "Application resume",
        location: applicationResumeLocation,
        readOnly: true,
      },
      {
        key: "user",
        label: "User resume",
        location: userResumeLocation,
        readOnly: false,
      },
    ]
      .filter(({ location }) => !!location)
      .map((tab) => {
        const { pdfUrl, originalUrl } = getResumeUrls(tab.location, {
          withCacheBusting: true,
        });

        return {
          ...tab,
          resumeUrl: pdfUrl,
          originalResumeUrl: originalUrl,
        };
      });
  }, [applicationResumeLocation, userResumeLocation]);

  useEffect(() => {
    setPdfErrors({});
    setActiveResumeKey(resumeTabs[0]?.key || "user");
  }, [resumeTabs]);

  if (!currentUser) {
    return null;
  }

  const activeResume =
    resumeTabs.find(({ key }) => key === activeResumeKey) || resumeTabs[0];
  const hasMultipleResumes = resumeTabs.length > 1;
  const canUploadResume = onInitiateUpload && !activeResume?.readOnly;

  const nameForTitle =
    [currentUser.given_name, currentUser.family_name]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join(" ") || getUserName(currentUser);
  const headerTitle = nameForTitle ? `${nameForTitle} Resume` : "Resume";
  const iframeLabelName = nameForTitle || "Resume";

  const handleDownload = () => {
    if (activeResume?.resumeUrl) {
      window.open(activeResume.resumeUrl, "_blank");
    }
  };

  const handlePdfError = () => {
    if (activeResume?.key) {
      setPdfErrors((prev) => ({ ...prev, [activeResume.key]: true }));
    }
  };

  const actionLinkClass =
    "inline-flex h-auto items-center gap-1.5 rounded-md px-1.5 py-1 font-rubik text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 disabled:opacity-50";

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[90vw] w-[90vw] h-[90vh] max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden rounded-lg [&>button.absolute]:hidden">
          <DialogHeader className="h-fit shrink-0 space-y-0 border-b border-gray-200 bg-white px-6 py-4 sm:rounded-t-lg flex flex-row items-center justify-between">
            <div className="flex min-w-0 flex-1 items-center gap-2 pr-4 text-left">
              <FileUser
                className="h-5 w-5 shrink-0 text-gray-700"
                strokeWidth={1.75}
                aria-hidden
              />
              <DialogTitle className="font-rubik m-0 truncate text-base font-medium text-gray-700 sm:text-left">
                {headerTitle}
              </DialogTitle>
            </div>

            <div className="flex shrink-0 items-center">
              {activeResume?.readOnly && (
                <span className="mr-3 rounded-md bg-gray-100 px-2 py-1 font-rubik text-xs font-medium text-gray-600">
                  Read-only
                </span>
              )}

              {activeResume?.originalResumeUrl && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() =>
                      window.open(activeResume.originalResumeUrl, "_blank")
                    }
                    className={actionLinkClass}
                    title={CTA_TITLES.downloadOriginalFile}
                  >
                    <Download className="h-4 w-4 shrink-0" />
                    <span className="whitespace-nowrap">
                      Download original file
                    </span>
                  </Button>
                  <div
                    className="mx-3 h-6 w-px bg-gray-300"
                    role="separator"
                    aria-orientation="vertical"
                  />
                </>
              )}

              {canUploadResume && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => onInitiateUpload(!!userResumeLocation)}
                    className={actionLinkClass}
                    title={CTA_TITLES.uploadResume}
                  >
                    <Upload className="h-4 w-4 shrink-0" />
                    <span className="whitespace-nowrap">Upload Resume</span>
                  </Button>

                  {activeResume?.resumeUrl && (
                    <div
                      className="mx-3 h-6 w-px bg-gray-300"
                      role="separator"
                      aria-orientation="vertical"
                    />
                  )}
                </>
              )}

              {activeResume?.resumeUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleDownload}
                  className={actionLinkClass}
                  title={CTA_TITLES.downloadResume}
                >
                  <ExternalLink className="h-4 w-4 shrink-0" />
                  <span className="whitespace-nowrap">Open</span>
                </Button>
              )}

              <button
                type="button"
                onClick={() => onClose(false)}
                className="grid h-6 w-6 ml-4 shrink-0 place-items-center rounded-full bg-gray-500 text-white transition hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
                aria-label="Close"
              >
                <X className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>
          </DialogHeader>

          {hasMultipleResumes && (
            <Tabs
              value={activeResumeKey}
              onValueChange={setActiveResumeKey}
              className="shrink-0 border-b border-gray-200 bg-white px-6 py-3"
            >
              <TabsList className="h-9 justify-start rounded-md bg-gray-100 p-1">
                {resumeTabs.map(({ key, label }) => (
                  <TabsTrigger
                    key={key}
                    value={key}
                    className="h-7 px-3 font-rubik text-sm text-gray-600 data-[state=active]:bg-white data-[state=active]:text-gray-800"
                  >
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}

          <div className="flex-1 min-h-0 relative overflow-hidden bg-gray-100">
            {activeResume?.resumeUrl && !pdfErrors[activeResume?.key] ? (
              <iframe
                src={`${activeResume.resumeUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                className="w-full h-full border-0"
                title={`${
                  activeResume?.label || "Resume"
                } - ${iframeLabelName}`}
                onError={handlePdfError}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-gray-600 mb-4">
                    {pdfErrors[activeResume?.key]
                      ? "Unable to load resume preview"
                      : "No resume available"}
                  </p>
                  {activeResume?.resumeUrl && pdfErrors[activeResume?.key] && (
                    <Button
                      variant="outline"
                      onClick={handleDownload}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ResumePreviewModal;
